import { inject, injectable, multiInject, optional } from 'inversify'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { UnsavedWork } from '@core/interfaces/unsaved-work'
import type { WindowGateway } from '@core/services/window.gateway'

/**
 * Installs the one warning the platform actually supports: the browser's own
 * confirm before the document goes away.
 *
 * A Presenter because it owns a live browser subscription, and an AppLifecycle
 * participant so registering it cost one line in the composition root (§R).
 *
 * It answers a question and nothing more. Participants register against
 * Types.IUnsavedWork and are asked at unload time; none of them knows the
 * browser is involved, and this does not know what any of them holds.
 */
@injectable()
export class ExitGuardPresenter implements AppLifecycle {
  @multiInject(Types.IUnsavedWork)
  @optional()
  private participants: UnsavedWork[] = []

  @inject(Types.IWindowGateway)
  private windowGateway!: WindowGateway

  private watchesBeforeUnload = false

  get hasUnsavedWork(): boolean {
    return this.participants.some((participant) => participant.hasUnsavedWork())
  }

  /** After the user has said they are leaving anyway. */
  discardAll(): void {
    this.participants.forEach((participant) => participant.discardUnsavedWork())
  }

  /**
   * beforeUnload is an application-lifetime effect — the only thing that ends
   * it is the document going away, which also removes the listener. Guarded
   * registration rather than a disposer nobody could call.
   */
  initialize(): void {
    if (this.watchesBeforeUnload) return

    this.windowGateway.onBeforeUnload(() => this.hasUnsavedWork)
    this.watchesBeforeUnload = true
  }
}
